import { useState } from 'react';
import { SIZE_LABELS } from '../design/products';
import { previewUrl, renderSavedPreview, simulateOrder, uploadCustomerCapture } from '../services/api';
import { track } from '../services/analytics';
import { capturePoster } from '../services/capture';
import { addToCart, formatPrice, type Price } from '../services/shopify';
import { IconArrow, IconCheck, IconLink } from './icons';
import { useEditor } from './store';
import type { SyncState } from './useCloudSync';
import { Modal } from './ui';

export const editUrl = (id: string) => {
  const url = new URL(location.href);
  url.search = '';
  url.hash = '';
  url.searchParams.set('design', id);
  return url.toString();
};

export default function CartBar({ price, sync }: { price: Price; sync: SyncState }) {
  const { state } = useEditor();
  const { product } = state.design;
  const [busy, setBusy] = useState<null | 'save' | 'cart'>(null);
  const [error, setError] = useState<string | null>(null);
  const [share, setShare] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [devCart, setDevCart] = useState<{ id: string; payload: object; preview: string; adminUrl?: string } | null>(null);

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      setShare(editUrl(await sync.saveNow()));
      track('designer_saved');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setBusy(null);
    }
  };

  const order = async () => {
    setBusy('cart');
    setError(null);
    try {
      // Save and capture the screen at the same time; the capture only needs the DOM.
      const [id, capture] = await Promise.all([sync.saveNow(), capturePoster()]);
      // The capture is for the admin's comparison page: it is small, so wait for it (briefly) rather
      // than risk losing it when the page moves on. The thumbnail's address is fixed and the server
      // renders it on first request, so that call just warms it up and is not waited for.
      if (capture) await Promise.race([uploadCustomerCapture(id, capture).catch(() => {}), new Promise((r) => setTimeout(r, 5000))]);
      renderSavedPreview(id).catch(() => {});
      const preview = previewUrl(id);
      // DYO and Preview are the property names the store, the order webhook and fulfilment already use.
      const line = { variantId: price.variantId, properties: { DYO: id, Preview: preview, _edit_url: editUrl(id) } };
      track('designer_add_to_cart', { size: product.size, framing: product.framing, style: state.design.style, value: price.amount ?? 0, currency: 'GBP' });
      try {
        const cartUrl = await addToCart(line);
        if (cartUrl) window.location.assign(cartUrl);
      } catch (e) {
        if (e instanceof Error && e.message === 'NOT_ON_STOREFRONT') {
          setDevCart({ id, payload: { options: product, price: price.amount, ...line }, preview });
        } else throw e;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add to cart');
    } finally {
      setBusy(null);
    }
  };

  const copy = async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* the field is selectable as a fallback */
    }
  };

  return (
    <div className="tmd-cart">
      {error && (
        <p className="tmd-alert" role="alert">
          {error}
        </p>
      )}
      <div className="tmd-cart-row">
        <button type="button" className="tmd-btn tmd-btn-ghost tmd-save" onClick={save} disabled={busy != null} title="Save and get a link to come back to this design">
          <IconLink /> <span>{busy === 'save' ? 'Saving…' : 'Save'}</span>
        </button>
        <button type="button" className="tmd-btn tmd-btn-primary" onClick={order} disabled={busy != null || !price.available}>
          <span className="tmd-cart-price">
            {formatPrice(price.amount)}
            <em>
              {SIZE_LABELS[product.size].short}″ · {product.framing === 'Unframed' ? 'Unframed' : 'Framed'}
            </em>
          </span>
          <span className="tmd-cart-cta">
            {busy === 'cart' ? 'Adding…' : 'Add to cart'} <IconArrow />
          </span>
        </button>
      </div>

      {share && (
        <Modal title="Design saved" onClose={() => setShare(null)}>
          <p>Use this link to pick up where you left off, on this device or any other. Changes you make from now on are saved automatically.</p>
          <div className="tmd-copy">
            <input className="tmd-input" readOnly value={share} onFocus={(e) => e.currentTarget.select()} aria-label="Link to your design" />
            <button type="button" className="tmd-btn" onClick={copy}>
              {copied ? (
                <>
                  <IconCheck /> Copied
                </>
              ) : (
                'Copy link'
              )}
            </button>
          </div>
          {typeof navigator.share === 'function' && (
            <button type="button" className="tmd-btn tmd-btn-ghost" onClick={() => navigator.share({ title: 'My Trail Map design', url: share }).catch(() => {})}>
              Send it to myself…
            </button>
          )}
        </Modal>
      )}

      {devCart && (
        <Modal title="Add to cart (local dev)" onClose={() => setDevCart(null)}>
          <p className="tmd-muted">Not running inside the store, so nothing was added to a real cart. On the storefront this is what gets sent to Shopify:</p>
          <div className="tmd-devcart">
            <img src={devCart.preview} alt="Cart thumbnail" />
            <pre>{JSON.stringify(devCart.payload, null, 2)}</pre>
          </div>
          {devCart.adminUrl ? (
            <p>
              Marked as ordered.{' '}
              <a href={devCart.adminUrl} target="_blank" rel="noreferrer">
                Open it in the admin
              </a>{' '}
              to compare this screen with the print file.
            </p>
          ) : (
            <button
              type="button"
              className="tmd-btn"
              onClick={() => simulateOrder(devCart.id).then((r) => setDevCart({ ...devCart, adminUrl: r.adminUrl }), (e) => setError(e.message))}
            >
              Simulate a completed checkout
            </button>
          )}
        </Modal>
      )}
    </div>
  );
}
